import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface Version {
  id: number;
  prompt_id: number;
  version_number: number;
  version_name: string | null;
  prompt_text: string;
  editor_email: string;
  editor_name: string | null;
  change_summary: string | null;
  created_at: string;
}

interface DiffLine {
  type: 'header' | 'add' | 'remove' | 'same';
  line: string;
}

interface PromptVersionHistoryProps {
  promptId: number;
  promptName: string;
  currentText: string;
  userEmail: string;
  userName?: string;
  onRestore?: (text: string) => void;
  onClose?: () => void;
}

export default function PromptVersionHistory({
  promptId,
  promptName,
  currentText,
  userEmail,
  userName,
  onRestore,
  onClose
}: PromptVersionHistoryProps) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersionA, setSelectedVersionA] = useState<Version | null>(null);
  const [selectedVersionB, setSelectedVersionB] = useState<Version | null>(null);
  const [diffLines, setDiffLines] = useState<DiffLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [diffLoading, setDiffLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'versions' | 'diff' | 'changelog'>('versions');
  const [changelog, setChangelog] = useState<any[]>([]);
  const [changelogLoading, setChangelogLoading] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [promptId]);

  useEffect(() => {
    if (activeTab === 'changelog') {
      loadChangelog();
    }
  }, [activeTab, promptId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/prompts/${promptId}/versions`);
      setVersions(response.data.versions || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const loadChangelog = async () => {
    try {
      setChangelogLoading(true);
      const response = await api.get(`/prompts/${promptId}/changelog`);
      setChangelog(response.data.entries || []);
    } catch (err: any) {
      console.error('Failed to load changelog:', err);
    } finally {
      setChangelogLoading(false);
    }
  };

  const loadDiff = async (versionA: Version, versionB: Version) => {
    try {
      setDiffLoading(true);
      setError(null);
      const response = await api.get(`/prompts/${promptId}/diff`, {
        params: {
          version_a: versionA.id,
          version_b: versionB.id
        }
      });
      setDiffLines(response.data.diff_lines || []);
      setActiveTab('diff');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load diff');
    } finally {
      setDiffLoading(false);
    }
  };

  const handleRestore = async (version: Version) => {
    if (!confirm(`Restore to version ${version.version_number}? This will create a backup of the current version first.`)) {
      return;
    }

    try {
      setRestoring(true);
      setError(null);

      await api.post(`/prompts/${promptId}/restore`, {
        version_id: version.id,
        editor_email: userEmail,
        editor_name: userName,
        restore_reason: `Restored to version ${version.version_number}`
      });

      setSuccessMessage(`Restored to version ${version.version_number}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Reload versions
      await loadVersions();

      // Notify parent to update
      if (onRestore) {
        onRestore(version.prompt_text);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const handleSelectForDiff = (version: Version) => {
    if (!selectedVersionA) {
      setSelectedVersionA(version);
    } else if (!selectedVersionB) {
      setSelectedVersionB(version);
      loadDiff(selectedVersionA, version);
    } else {
      // Reset and start over
      setSelectedVersionA(version);
      setSelectedVersionB(null);
      setDiffLines([]);
    }
  };

  const clearDiffSelection = () => {
    setSelectedVersionA(null);
    setSelectedVersionB(null);
    setDiffLines([]);
    setActiveTab('versions');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created': return 'bg-green-100 text-green-800';
      case 'updated': return 'bg-blue-100 text-blue-800';
      case 'restored': return 'bg-purple-100 text-purple-800';
      case 'deleted': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="spinner mr-2"></span>
        <span className="text-gray-500">Loading version history...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
          <p className="text-sm text-gray-500">{promptName} - {versions.length} versions (max 30)</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-ghost">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Error/Success Alerts */}
      {error && (
        <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">
          {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('versions')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'versions'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Versions ({versions.length})
          </button>
          <button
            onClick={() => setActiveTab('diff')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'diff'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Compare
          </button>
          <button
            onClick={() => setActiveTab('changelog')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'changelog'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Changelog
          </button>
        </div>
      </div>

      {/* Versions Tab */}
      {activeTab === 'versions' && (
        <div className="space-y-2">
          {/* Diff Selection Help */}
          {(selectedVersionA || selectedVersionB) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm text-blue-700">
                {selectedVersionA && !selectedVersionB
                  ? `Selected v${selectedVersionA.version_number} - click another version to compare`
                  : `Comparing v${selectedVersionA?.version_number} vs v${selectedVersionB?.version_number}`
                }
              </span>
              <button onClick={clearDiffSelection} className="text-blue-600 hover:text-blue-800 text-sm">
                Clear
              </button>
            </div>
          )}

          {versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No versions available yet.</p>
              <p className="text-sm mt-1">Save the prompt to create the first version.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className={`p-4 border rounded-lg transition-colors ${
                    selectedVersionA?.id === version.id || selectedVersionB?.id === version.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          v{version.version_number}
                        </span>
                        {version.version_name && (
                          <span className="text-sm text-gray-600">
                            - {version.version_name}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        <span>{version.editor_name || version.editor_email}</span>
                        <span className="mx-2">|</span>
                        <span>{formatDate(version.created_at)}</span>
                      </div>
                      {version.change_summary && (
                        <p className="mt-2 text-sm text-gray-600 italic">
                          "{version.change_summary}"
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleSelectForDiff(version)}
                        className="btn-ghost text-xs py-1 px-2"
                        title="Select for comparison"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Diff
                      </button>
                      <button
                        onClick={() => handleRestore(version)}
                        disabled={restoring}
                        className="btn-secondary text-xs py-1 px-2"
                      >
                        {restoring ? (
                          <span className="spinner w-3 h-3"></span>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                        Restore
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Diff Tab */}
      {activeTab === 'diff' && (
        <div className="space-y-4">
          {!selectedVersionA || !selectedVersionB ? (
            <div className="text-center py-8 text-gray-500">
              <p>Select two versions to compare.</p>
              <p className="text-sm mt-1">Go to the Versions tab and click "Diff" on two versions.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Comparing <strong>v{selectedVersionA.version_number}</strong> to <strong>v{selectedVersionB.version_number}</strong>
                </span>
                <button onClick={clearDiffSelection} className="text-brand-600 hover:text-brand-800">
                  Clear
                </button>
              </div>

              {diffLoading ? (
                <div className="flex items-center justify-center py-8">
                  <span className="spinner mr-2"></span>
                  <span className="text-gray-500">Loading diff...</span>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden bg-gray-50">
                  <div className="p-4 font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
                    {diffLines.length === 0 ? (
                      <p className="text-gray-500 text-center">No differences found</p>
                    ) : (
                      diffLines.map((line, idx) => (
                        <div
                          key={idx}
                          className={`whitespace-pre-wrap ${
                            line.type === 'add' ? 'bg-green-100 text-green-800' :
                            line.type === 'remove' ? 'bg-red-100 text-red-800' :
                            line.type === 'header' ? 'bg-gray-200 text-gray-700 font-semibold' :
                            'text-gray-700'
                          }`}
                        >
                          {line.type === 'add' && <span className="text-green-600">+ </span>}
                          {line.type === 'remove' && <span className="text-red-600">- </span>}
                          {line.type === 'same' && <span className="text-gray-400">  </span>}
                          {line.line}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Changelog Tab */}
      {activeTab === 'changelog' && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {changelogLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="spinner mr-2"></span>
              <span className="text-gray-500">Loading changelog...</span>
            </div>
          ) : changelog.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No changelog entries yet.</p>
            </div>
          ) : (
            changelog.map((entry) => (
              <div key={entry.id} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(entry.action)}`}>
                    {entry.action}
                  </span>
                  <span className="text-sm text-gray-700">
                    {entry.editor_name || entry.editor_email}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDate(entry.created_at)}
                  </span>
                </div>
                {entry.change_summary && (
                  <p className="mt-1 text-sm text-gray-600">{entry.change_summary}</p>
                )}
                {entry.version_id && (
                  <p className="mt-1 text-xs text-gray-400">Version ID: {entry.version_id}</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
